import { useCallback, useEffect, useRef, useState } from "react";
import { supabase, safeChannel } from "./supabase";

// useKidPetState — live pet state for a given kid user_id. Three
// responsibilities:
//
//   1. On mount, call ensure_kid_pet_state() RPC which idempotently
//      inserts a starter pet row if the kid doesn't have one yet, and
//      returns the lazy-decayed hunger value. The RPC short-circuits
//      for non-kid callers (auth.uid() mismatch handled by RLS).
//
//   2. Subscribe to realtime on kid_pet_state where user_id=<kid>.
//      Card-earn triggers on the server bump hunger + xp; we want the
//      pet to react live without a poll.
//
//   3. Expose a `setMood` debug helper for M1 development. Real card
//      grants happen via the cook_logs trigger (M2) and bypass this.
//
// No localStorage cache — pet state IS realtime by design. A stale
// cached hunger from an hour ago would render the wrong mood-face on
// boot. Boot fetch is one row, ~80 bytes, trivial egress.

export function useKidPetState(kidUserId) {
  const [pet, setPet] = useState(null);     // { pet_slug, hunger, level, image_url, ... }
  const [activeStats, setActiveStats] = useState(EMPTY_ACTIVE);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const channelRef = useRef(null);

  const loadActive = useCallback(async () => {
    if (!kidUserId) { setActiveStats(EMPTY_ACTIVE); return; }
    const { data, error } = await supabase.rpc("get_active_card_stats", { p_kid_user_id: kidUserId });
    if (error) {
      console.warn("[kid-pet] get_active_card_stats:", error.message || error);
      return;
    }
    const row = Array.isArray(data) ? data[0] : data;
    setActiveStats(row ? {
      boost: row.active_boost || 0,
      spark: row.active_spark || 0,
      build: row.active_build || 0,
      super: row.active_super || 0,
      dairy: row.active_dairy || 0,
      earliestAt: row.earliest_at || null,
    } : EMPTY_ACTIVE);
  }, [kidUserId]);

  const load = useCallback(async () => {
    if (!kidUserId) {
      setPet(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    const { data, error } = await supabase.rpc("ensure_kid_pet_state", { p_kid_id: kidUserId });
    if (error) {
      console.error("[kid-pet] ensure_kid_pet_state:", error);
      setError(error);
      setLoading(false);
      return;
    }
    const row = Array.isArray(data) ? data[0] : data;
    setPet(row || null);
    setLoading(false);
    loadActive();
  }, [kidUserId, loadActive]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!kidUserId) return;
    const ch = safeChannel(`rt:kid_pet:${kidUserId}`)
      .on("postgres_changes",
        { event: "*", schema: "public", table: "kid_pet_state", filter: `user_id=eq.${kidUserId}` },
        () => { load(); })
      .subscribe();
    channelRef.current = ch;
    return () => {
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
    };
  }, [kidUserId, load]);

  // Active-stat decay tick. Stats fade as spend timestamps age past
  // the 3h window; we re-fetch every 60s so the bars decay on screen
  // without the kid having to navigate away. Skip when no buff is
  // active (earliestAt null) — nothing to decay.
  useEffect(() => {
    if (!kidUserId) return;
    if (!activeStats.earliestAt) return;
    const t = setInterval(() => { loadActive(); }, 60_000);
    return () => clearInterval(t);
  }, [kidUserId, activeStats.earliestAt, loadActive]);

  const mood = (() => {
    const h = pet?.hunger ?? 80;
    if (h >= 80) return "happy";
    if (h >= 50) return "content";
    if (h >= 30) return "calm";
    return "sleepy";
  })();

  return { pet, mood, activeStats, loading, error, refresh: load, refreshActive: loadActive };
}

const EMPTY_ACTIVE = { boost: 0, spark: 0, build: 0, super: 0, dairy: 0, earliestAt: null };
